import React from 'react';
import { FileText, Download, Eye } from 'lucide-react';
import { colors, FONT } from '../../theme';
import { fetchFileObjectURL } from '../../services/fileFetch';

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' Б';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' КБ';
  return (bytes / (1024 * 1024)).toFixed(1) + ' МБ';
}

// Extensions/mimes that can be previewed in-app
const PREVIEWABLE = /\.(pdf|jpg|jpeg|png|gif|webp)$/i;
const PREVIEWABLE_MIME = /^(application\/pdf|image\/)/;

function canPreview(name, mime) {
  if (mime && PREVIEWABLE_MIME.test(mime)) return true;
  if (name && PREVIEWABLE.test(name)) return true;
  return false;
}

function guessMime(fileName) {
  if (!fileName) return 'application/octet-stream';
  const ext = fileName.split('.').pop().toLowerCase();
  const map = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
    gif: 'image/gif', webp: 'image/webp',
    doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };
  return map[ext] || 'application/octet-stream';
}

export default function FileMessage({ fileId, fileKey, fileMime, fileName, fileSize, isMine, onPreview }) {
  const mime = fileMime || guessMime(fileName);
  const previewable = canPreview(fileName, mime);

  const handleClick = () => {
    if (previewable && onPreview) {
      onPreview(fileId, fileName, mime);
    } else {
      download();
    }
  };

  const download = (e) => {
    if (e) e.stopPropagation();
    fetchFileObjectURL(fileId, fileKey, mime)
      .then(url => {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'file';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      })
      .catch((err) => {
        // A tap that does nothing at all is indistinguishable from a tap that
        // was not registered. Say what happened.
        console.warn('[FileMessage] download failed:', err.message);
        alert(/404/.test(err.message) ? 'Файл больше не доступен' : 'Не удалось скачать файл');
      });
  };

  const accent = isMine ? colors.avatarBlue : colors.gray500;

  return (
    <div
      onClick={handleClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        cursor: 'pointer', minWidth: 160, padding: '4px 0',
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: isMine ? 'rgba(91,141,239,0.15)' : colors.gray200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <FileText size={20} color={accent} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 500, color: colors.gray900,
          fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {fileName || 'Файл'}
        </div>
        <div style={{ fontSize: 11, color: colors.gray400, fontFamily: FONT, marginTop: 1 }}>
          {formatBytes(fileSize)}
        </div>
      </div>
      {previewable ? (
        <Eye size={16} color={accent} style={{ flexShrink: 0 }} />
      ) : (
        <Download size={16} color={accent} style={{ flexShrink: 0 }} />
      )}
    </div>
  );
}
